import React, { Component, useCallback, useContext, useEffect, useState } from 'react';
import Select from 'react-select';
import { SearchType } from '../types/search.type';
import { AppContext } from '../context';
import ObjectDataService from '../services/object.service';
import useDebounce from '../hook/debounce.hook';
import { SuggestionsType } from '../types/suggestions.type';
import Text from './text.component';
import propertyTypes from '../json/property-types.json';
import priceValues from '../json/prices-options.json';
import transactionValues from '../json/transactions-options.json';
import '../styles/search.css';
import '../styles/select.css';
import Filters from './filters.component';
import TranslationService from '../services/translation.service';

function withHooks(WrappedComponent: typeof Search) {
  return function (props: any) {
    const context = useContext(AppContext);
    const [search, setSearch] = useState('');
    const [reference, setReference] = useState('');
    const [autocomplete, setAutocomplete] = useState('');
    const debouncedAutocomplete = useDebounce<string>(autocomplete, 300);
    const [cachedSuggestions, setCachedSuggestions] = useState<any>({});
    const [placeId, setPlaceId] = useState(null);
    const [priceRanges, setPriceRanges] = useState([]);
    const [typeIds, setTypeIds] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [autocompleteDescription, setAutocompleteDescription] = useState('');

    const retrieveObjects = () => {
      const { state, dispatch } = context;
      if (state.status === "not-started") {
        state.status = "loading";
        dispatch({
          type: "setStatus",
          payload: {
            status: "loading"
          }
        });
        ObjectDataService.search(state.search)
          .then((response: any) => {
            state.results = response.data;
            dispatch({
              type: "setResults",
              payload: {
                results: response.data
              }
            });
            state.status = "success";
            dispatch({
              type: "setStatus",
              payload: {
                status: "success"
              }
            });
          })
          .catch((e: Error) => {
            state.status = "error";
            dispatch({
              type: "setStatus",
              payload: {
                status: "error"
              }
            });
          });
      }
    }

    const retrieveAutocomplete = useCallback((value: string) => {
      const { state, dispatch } = context;
      if (value === '') {
        if (state.suggestions && state.suggestions.places.length > 0) {
          dispatch({
            type: "setSuggestions",
            payload: {
              suggestions: {
                places: []
              }
            }
          });
        }
        return;
      }
      if (cachedSuggestions[value]) {
        if (state.suggestions === cachedSuggestions[value]) {
          return;
        }

        dispatch({
          type: "setSuggestions",
          payload: {
            suggestions: cachedSuggestions[value] as SuggestionsType
          }
        });
        return;
      }

      ObjectDataService.autocomplete({ search: value })
        .then((response: any) => {
          setCachedSuggestions({
            ...cachedSuggestions,
            [value]: response.data as SuggestionsType
          });
          dispatch({
            type: "setSuggestions",
            payload: {
              suggestions: response.data as SuggestionsType
            }
          });
        })
        .catch((e: Error) => {
          dispatch({
            type: "setSuggestions",
            payload: {
              suggestions: {
                places: []
              }
            }
          });
        });
    }, [context, cachedSuggestions]);

    useEffect(() => {
      retrieveAutocomplete(debouncedAutocomplete);
    }, [retrieveAutocomplete, debouncedAutocomplete]);

    return (
      <WrappedComponent
        retrieveObjects={retrieveObjects}
        retrieveAutocomplete={retrieveAutocomplete}
        search={search}
        setSearch={setSearch}
        reference={reference}
        setReference={setReference}
        transactions={transactions}
        setTransactions={setTransactions}
        autocomplete={autocomplete}
        setAutocomplete={setAutocomplete}
        placeId={placeId}
        setPlaceId={setPlaceId}
        typeIds={typeIds}
        setTypeIds={setTypeIds}
        priceRanges={priceRanges}
        setPriceRanges={setPriceRanges}
        autocompleteDescription={autocompleteDescription}
        setAutocompleteDescription={setAutocompleteDescription}
        {...props} />
    );
  }
}

class Search extends Component<{ search?: SearchType }> {
  static contextType = AppContext;
  context!: React.ContextType<typeof AppContext>;
  props: any;

  componentDidMount() {
    const { dispatch, state } = this.context;
    if (state.results?.nb === undefined || state.results?.nb <= 0) {
      const queryParameters = new URLSearchParams(window.location.search);

      const { setTypeIds, setPriceRanges, setPlaceId, setAutocompleteDescription, setSearch, setReference, setTransactions } = this.props;
      var searchPayload = { ...state.search };
      var hasSearch = false;

      const searchParam = queryParameters.get("search");
      if (searchParam && searchParam !== '') {
        searchPayload.search = searchParam;
        setSearch(searchParam);
        hasSearch = true;
      }

      const referenceParam = queryParameters.get("reference");
      if (referenceParam && referenceParam !== '') {
        searchPayload.reference = referenceParam;
        setReference(referenceParam);
        hasSearch = true;
      }

      const typeIdsParam = queryParameters.get("type");
      if (typeIdsParam && typeIdsParam !== '') {
        searchPayload.typeIds = typeIdsParam.split(',').map((typeId) => parseInt(typeId));
        setTypeIds(typeIdsParam.split(',').map((typeId: any) => {
          var propertyType: any = propertyTypes.filter((type: any) => {
            return type.value === parseInt(typeId);
          });
          if (propertyType === null) {
            return null;
          }
          propertyType = propertyType[0];
          propertyType.label = TranslationService.getMessage(propertyType.text, this.context.state.language);
          return propertyType;
        }).filter((type) => { return type !== null; }));
        hasSearch = true;
      }

      const priceRangesParam = queryParameters.get("prices");
      if (priceRangesParam && priceRangesParam !== '') {
        var priceRanges: any = priceRangesParam.split(',').map((priceRangeParam: any) => {
          var priceRange: any = priceValues.filter((priceValue: any) => {
            return priceValue.min + '-' + priceValue.max === priceRangeParam;
          });
          if (priceRange === null || priceRange.length === 0) {
            return null;
          }
          priceRange = priceRange[0];
          priceRange.value = priceRange;
          priceRange.label = TranslationService.getMessage(priceRange.text, this.context.state.language);
          return priceRange;
        }).filter((priceRange) => { return priceRange !== null; });
        searchPayload.priceRanges = priceRanges.map((priceRange: any) => {
          return {
            min: parseInt(priceRange.value.min),
            max: parseInt(priceRange.value.max)
          }
        });
        setPriceRanges(priceRanges);
      }

      const placeIdParam = queryParameters.get("place");
      if (placeIdParam && placeIdParam !== '') {
        searchPayload.placeId = placeIdParam;
        setPlaceId(placeIdParam);
        hasSearch = true;
      }

      const autocompleteParam = queryParameters.get("placeName");
      if (autocompleteParam && autocompleteParam !== '') {
        setAutocompleteDescription(autocompleteParam);
        hasSearch = true;
      }

      const transactionParam = queryParameters.get("transaction");
      if (transactionParam && transactionParam !== '') {
        searchPayload.transactions = transactionParam.split(',');
        setTransactions(transactionParam.split(',').map((transactionValue: any) => {
          var transactionObject: any = transactionValues.filter((transaction: any) => {
            return transaction.value === transactionValue;
          })
          if (! transactionObject || transactionObject.length === 0) {
            return null;
          }
          transactionObject = transactionObject[0];
          transactionObject.label = TranslationService.getMessage(transactionObject.text, this.context.state.language);
          return transactionObject;
        }).filter((type) => { return type !== null; }));
        hasSearch = true;
      }

      dispatch({
        type: "setSearch",
        payload: {
          search: searchPayload
        }
      });
      if (hasSearch) {
        let resultDivs = document.getElementsByClassName('layout-results');
        if (resultDivs.length > 0) {
          window.scrollTo({ top: resultDivs[0].getBoundingClientRect().top, left: 0, behavior: 'smooth' });
        }
      }
    }
  }

  componentDidUpdate() {
    const { retrieveObjects } = this.props;
    retrieveObjects();
  }

  applyStateToContext() {
    const { state, dispatch } = this.context;
    const { typeIds, placeId, search, reference, transactions, priceRanges } = this.props;
    dispatch({
      type: "setSearch",
      payload: {
        search: {
          ...state.search,
          search: (search ? search : ''),
          reference: (reference ? reference : ''),
          typeIds: (typeIds ? typeIds.map((typeId: any) => { return typeId.value }) : []),
          priceRanges: (priceRanges ? priceRanges.map((priceRange: any) => { return { min: priceRange.value.min, max: priceRange.value.max }; }) : []),
          placeId: placeId,
          transactions: (transactions ? transactions.map((transactionValue: any) => { return transactionValue.value }) : [])
        }
      }
    });
  }

  search() {
    const searchParams = new URLSearchParams(window.location.search || '');
    const { typeIds, placeId, autocompleteDescription, redirect, search, reference, transactions, priceRanges } = this.props;

    if (search && search !== '') {
      searchParams.set("search", search);
    } else {
      searchParams.delete('search');
    }

    if (reference && reference !== '') {
      searchParams.set("reference", reference);
    } else {
      searchParams.delete('reference');
    }

    if (typeIds && typeIds.length > 0) {
      searchParams.set("type", typeIds.map((typeId: any) => {
        return typeId.value;
      }).join(','));
    } else {
      searchParams.delete('type');
    }

    if (priceRanges && priceRanges.length > 0) {
      searchParams.set("prices", priceRanges.map((priceRange: any) => {
        return priceRange.value.min + '-' + priceRange.value.max;
      }).join(','));
    } else {
      searchParams.delete('prices');
    }

    if (placeId && placeId !== '') {
      searchParams.set("place", placeId);
    } else {
      searchParams.delete('place');
    }

    if (autocompleteDescription && autocompleteDescription !== '') {
      searchParams.set("placeName", autocompleteDescription);
    } else {
      searchParams.delete('placeName');
    }

    if (transactions && transactions.length > 0) {
      searchParams.set("transaction", transactions.map((transaction: any) => {
        return transaction.value;
      }).join(','));
    } else {
      searchParams.delete('transaction');
    }

    if (redirect) {
      const { state } = this.context;
      var url = 'https://' + window.location.host + '/' + state.language + '/luxury-properties/search';
      window.location.href = url + '?' + searchParams.toString();
      return;
    }

    window.history.pushState({ reload: true }, 'Searching', `${window.location.pathname}?${searchParams.toString()}`);

    this.applyStateToContext();
    const { dispatch } = this.context;
    dispatch({
      type: "setStatus",
      payload: {
        status: "not-started"
      }
    });
    let resultDivs = document.getElementsByClassName('layout-results');
    if (resultDivs.length > 0) {
      window.scrollTo({ top: resultDivs[0].getBoundingClientRect().top, left: 0, behavior: 'smooth' });
    }
  }

  render() {
    const { state } = this.context;
    const { isTablet, autocomplete, setAutocomplete, typeIds, setTypeIds, setPlaceId, autocompleteDescription, setAutocompleteDescription, priceRanges, setPriceRanges } = this.props;
    return <div className={'search-container ' + (this.props.isMobile ? 'mobile' : (this.props.isTablet ? 'tablet' : 'desktop'))}>
      <div className='search-field autocomplete'>
        <span className='icon'>
          <svg version="1.1" width={30} id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"
            viewBox="0 0 55 73">
            <path className="st0" style={{
              fillRule: "evenodd",
              clipRule: "evenodd",
              fill: "#AFA682"
            }} d="M40.1,27.3c0,7.1-5.9,12.6-12.6,12.6c-7.2,0-12.5-5.9-12.5-12.6c0-7.2,5.7-12.4,12.5-12.4
              C34.6,14.9,40.1,20.6,40.1,27.3z M55,27.3c0,3-0.7,6.3-1.7,9.4c-0.7,2.4-2.3,5.7-3.6,8.3c-1.9,3.6-4.4,7.7-7.2,11.3
              c-1.6,2.1-3.4,4.4-5.3,6.7c-0.7,0.9-8.3,10-9.8,10c-1,0-3.6-3.3-3.9-3.4c-6.6-7.1-12.1-14.1-16.4-21C4.6,44.4,0,34.3,0,29.1v-3.6
              C1.7,8.7,14.9,0,27.4,0C43.1,0,55,12.6,55,27.3z"/>
            <circle className="st1" cx="27.2" cy="27.4" r="21" style={{
              fillRule: "evenodd",
              clipRule: "evenodd",
              fill: "#FFFFFF"
            }}/>
              <g
                id="g30"
                transform="matrix(0.16794626,0,0,0.16794626,-9.7481769,2.8462571)">
                <path
                    d="m 220,44.498 c 56.025,0 101.702,45.32 101.702,101.702 0,56.382 -45.677,101.345 -101.702,101.345 -56.025,0 -101.702,-45.32 -101.702,-101.345 0,-56.025 45.32,-101.702 101.702,-101.702 M 220,42 c -57.453,0 -104.2,46.747 -104.2,104.2 0,57.453 46.747,104.2 104.2,104.2 57.453,0 104.2,-46.747 104.2,-104.2 C 324.2,88.747 277.096,42 220,42 Z"
                    style={{
                      clipRule: "evenodd",
                      fill: "#afa682",
                      fillRule: "evenodd"
                    }} />
                <path
                    d="m 173.61,105.162 c -0.714,0 -0.714,-0.357 -0.714,-0.713 0,-0.714 0.357,-2.498 1.784,-2.498 5.353,0 10.349,0.357 15.701,0.357 4.64,0 9.279,-0.357 13.918,-0.357 0.713,0 1.07,0 1.07,0.713 0,2.498 -0.714,2.855 -3.568,2.855 -6.424,0.714 -7.137,1.784 -9.992,15.702 -1.07,5.71 -11.062,54.954 -11.062,60.664 0,4.639 4.282,4.639 9.634,4.639 22.482,0 27.121,-5.353 29.262,-12.133 0.714,-2.141 0.714,-2.498 1.428,-2.498 0.714,0 2.14,0 2.14,1.07 0,0.358 -1.784,7.495 -2.854,10.706 -2.141,6.067 -2.855,7.137 -9.635,7.137 -8.921,0 -17.843,-0.357 -26.764,-0.357 -11.776,0 -18.913,0.357 -23.909,0.357 -0.357,0 -0.713,0 -0.713,-0.714 0,-2.497 1.07,-2.497 3.568,-2.854 6.423,-0.714 7.137,-1.785 9.992,-15.702 1.07,-5.352 11.062,-54.24 11.062,-59.95 -0.713,-5.353 -3.925,-5.71 -10.348,-6.424 z"
                    style={{
                      clipRule: "evenodd",
                      fill: "#afa682",
                      fillRule: "evenodd"
                    }} />
                <path
                    d="m 207.51,119.793 c 0,-14.274 10.706,-18.556 21.768,-18.556 1.427,0 6.067,0 10.706,0.357 4.639,0 8.564,0.357 10.705,0.357 4.64,0 9.635,-0.357 14.274,-0.357 1.427,0 1.784,1.784 1.784,2.498 0,0.357 0,0.357 -0.713,0.713 -6.067,0.714 -9.635,1.071 -9.635,6.78 0,5.353 9.991,54.599 11.062,59.951 2.855,13.917 3.568,14.988 9.992,15.702 2.855,0.357 3.568,0.357 3.568,2.854 0,0.714 -0.357,0.714 -0.713,0.714 -5.353,0 -10.35,-0.357 -15.702,-0.357 -5.71,0 -11.776,0.357 -17.485,0.357 -1.428,0 -1.785,-1.784 -1.785,-2.498 0,-0.713 0.357,-0.713 3.212,-0.713 6.066,-0.357 10.705,-0.357 10.705,-6.424 0,-6.067 -12.846,-69.229 -13.203,-70.656 -0.714,-2.498 -1.427,-4.639 -10.349,-4.639 -11.419,0 -17.842,4.64 -17.842,15.701 0,10.349 6.423,21.768 24.266,21.768 3.925,0 3.925,0 3.925,1.07 0,2.856 -1.07,2.856 -4.282,2.856 -18.556,-0.357 -34.258,-10.706 -34.258,-27.478 z"
                    style={{
                      clipRule: "evenodd",
                      fill: "#afa682",
                      fillRule: "evenodd"
                    }} />
              </g>
          </svg>
        </span>
        <Text text={'Search'} />
        <input
          placeholder={(autocompleteDescription !== '' ? autocompleteDescription : TranslationService.getMessage('Search by City, Region...', this.context.state.language))}
          type="text"
          value={autocomplete}
          onChange={(e) => setAutocomplete(e.currentTarget.value)} />
        {autocompleteDescription &&
          <button
            className='reset-button'
            onClick={() => {
              setAutocomplete('');
              setAutocompleteDescription('');
              setPlaceId(null);
            }}/>}
        {state.suggestions && state.suggestions.places.length > 0 &&
          <div className='suggestions'>
            {state.suggestions.places.map((place, key) => {
              return <div key={key} className='suggestion' onClick={(e) => {
                setAutocomplete('');
                setAutocompleteDescription(place.description);
                setPlaceId(place.placeId);
              }}>
                <span className='description'>{place.description}</span>
                &nbsp;
                <span className='type'>
                  (<Text text={place.type} />)
                </span>
              </div>
            })}
          </div>
        }
      </div>

      {!isTablet &&
        <>
          <div className='search-field property'>
            <Text text={'Type'} />
            <Select
              value={typeIds}
              isClearable={true}
              isMulti={true}
              isSearchable={false}
              hideSelectedOptions={false}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder={TranslationService.getMessage('All property types', this.context.state.language)}
              onChange={(values) => setTypeIds(values)}
              options={propertyTypes.map((type: any) => {
                return {
                  value: type.value,
                  label: TranslationService.getMessage(type.text, this.context.state.language)
                };
              })} />
          </div>

          <div className='search-field price'>
            <Text text={'Price'} />
            <Select
              value={priceRanges}
              isClearable={true}
              isMulti={true}
              isSearchable={false}
              hideSelectedOptions={false}
              className="react-select-container"
              classNamePrefix="react-select"
              placeholder={TranslationService.getMessage('All prices', this.context.state.language)}
              onChange={(values) => setPriceRanges(values)}
              options={priceValues.map((priceValue: any) => {
                return {
                  value: priceValue,
                  label: TranslationService.getMessage(priceValue.text, this.context.state.language)
                };
              })} />
          </div>
        </>
      }

      <Filters
        doSearch={this.search.bind(this)}
        {...this.props} />

      <div className='search'>
        <button onClick={this.search.bind(this)} className='btn btn-default btn-search btn-primary'>
          <img
            alt={TranslationService.getMessage('Search', this.context.state.language)}
            src="https://www.luxury-places.ch/themes/custom/luxuryplaces/img/icons/zoom.svg" />
        </button>
      </div>
    </div>
  }
}
export default withHooks(Search);
